import React, { useRef, useState } from "react";
import {
  View,
  Modal,
  Platform,
  StyleSheet,
  SafeAreaView,
  PermissionsAndroid,
} from "react-native";
import MapView, { Region } from "react-native-maps";
import Geolocation from "react-native-geolocation-service";

import PermissionBlockedAlert from "components/boxes/PermissionBlockedAlert";
import ImgIcon from "components/imgIcon";
import Button from "components/Button";
import Toast from "components/toast";

import Loading from "components/Loading";

import { colors, strings } from "assets/constants";
import { apiKeys } from "assets/data";
import { icons } from "assets/icons";
import { mq } from "utils";

const pinSize = 70;

const lat = 33.98702662264141;
const lng = 51.44273149139045;
const initialRegion: Region = {
  latitude: lat,
  longitude: lng,
  latitudeDelta: 0.01,
  longitudeDelta: 0.002,
};

interface CoordinateT {
  lat: string;
  lng: string;
}

export default function Map(props: {
  visible: boolean;
  hide: () => void;
  initialCoordinate?: CoordinateT;
  onConfirmLocation?: (coordinate: CoordinateT) => void;
}) {
  const { hide, visible, initialCoordinate, onConfirmLocation } = props;

  const initial = initialCoordinate
    ? {
        ...initialRegion,
        latitude: +initialCoordinate.lat,
        longitude: +initialCoordinate.lng,
      }
    : initialRegion;

  const [coordinate, setCoordinate] = useState<Region>(initial);

  const [blockedAlert, setBlockedAlert] = useState(false);
  const showBlockedAlert = () => setBlockedAlert(true);
  const hideBlockedAlert = () => setBlockedAlert(false);
  const mapRef = useRef<MapView>(null);

  async function _getLocationPermission() {
    const granted = await PermissionsAndroid.request(
      "android.permission.ACCESS_FINE_LOCATION"
    );

    if (granted === "never_ask_again") {
      showBlockedAlert();
      return;
    }

    if (granted === "denied") {
      Toast.warning({ message: strings.app_not_allowed_location_permission });
      return;
    }

    return Promise.resolve();
  }

  const _onLoad = () => {
    if (initialCoordinate) {
      _getLocationPermission();
    } else {
      _onUserLocation();
    }
  };

  const _onUserLocation = () => {
    _getLocationPermission().then(() => {
      Geolocation.getCurrentPosition((currentLoc) => {
        const { latitude, longitude } = currentLoc.coords;
        mapRef.current?.animateToRegion({
          ...initialRegion,
          latitude,
          longitude,
        });
      });
    });
  };

  const _onConfirm = () => {
    onConfirmLocation &&
      onConfirmLocation({
        lat: String(coordinate.latitude),
        lng: String(coordinate.longitude),
      });
    hide();
  };

  const _onChangeLocation = setCoordinate;

  return (
    <>
      <Modal
        visible={visible}
        animationType="fade"
        onRequestClose={hide}
        onShow={_onLoad}
      >
        <SafeAreaView style={styles.container}>
          <View style={styles.container}>
            <MapView
              provider="google"
              googleMapsApiKey={apiKeys.googleMap}
              loadingFallback={
                <View>
                  <Loading />
                </View>
              }
              initialRegion={initial}
              onRegionChange={_onChangeLocation}
              ref={mapRef}
              loadingEnabled
              rotateEnabled={false}
              showsUserLocation={true}
              showsMyLocationButton={false}
              style={styles.mapView}
              loadingBackgroundColor={colors.screensBg}
            />

            <Button
              imgIcon={{ icon: icons.focus, size: mq(25) }}
              width={mq(50)}
              height={mq(50)}
              absolute
              right={20}
              bottom={25}
              style={{
                zIndex: mq(1000),
                borderRadius: mq(50),
                borderWidth: mq(5),
                borderColor: colors.darkGlass,
              }}
              onPress={_onUserLocation}
            />

            <Button
              title={{ value: strings.cancel, size: "sm" }}
              absolute
              left={130}
              bottom={25}
              width={mq(55)}
              height={mq(50)}
              bgColor={colors.danger}
              style={{
                zIndex: mq(1000),
                borderRadius: mq(50),
                borderWidth: mq(2),
                borderColor: colors.darkGlass,
              }}
              onPress={hide}
            />

            <Button
              title={{ value: strings.confirm_location, size: "sm" }}
              width={100}
              height={mq(50)}
              bgColor={colors.success}
              absolute
              left={20}
              bottom={25}
              style={{
                zIndex: mq(1000),
                borderRadius: mq(50),
                borderWidth: mq(2),
                borderColor: colors.darkGlass,
              }}
              onPress={_onConfirm}
            />

            <View style={styles.mapMarkerContainer}>
              <ImgIcon size={mq(pinSize)} icon={icons.pin} />
            </View>
          </View>
        </SafeAreaView>
      </Modal>

      {Platform.OS === "android" && (
        <PermissionBlockedAlert
          visible={blockedAlert}
          hide={hideBlockedAlert}
          text={strings.loc_perm_denied_alert_box_desc}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },

  mapView: {
    position: "absolute",
    top: mq(0),
    left: mq(0),
    right: mq(0),
    bottom: mq(0),
    marginBottom: mq(0),
  },

  mapMarkerContainer: {
    width: mq(50),
    height: mq(50),
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: [{ translateX: mq(-pinSize / 2) }, { translateY: mq(-pinSize) }],
  },
});
